import { z } from "zod";
import { RoleSchema } from "./roles.js";

export const APPLICATION_TYPES = ["transfer", "reserve"] as const;
export const ApplicationTypeSchema = z.enum(APPLICATION_TYPES);
export type ApplicationType = z.infer<typeof ApplicationTypeSchema>;

export const APPLICATION_TEMPLATE_TYPES = [...APPLICATION_TYPES, "transfer_or_reserve"] as const;
export const ApplicationTemplateTypeSchema = z.enum(APPLICATION_TEMPLATE_TYPES);
export type ApplicationTemplateType = z.infer<typeof ApplicationTemplateTypeSchema>;

export const APPLICATION_TYPE_LABELS: Record<ApplicationTemplateType, string> = {
  transfer: "Transfer",
  reserve: "Reserve",
  transfer_or_reserve: "Transfer or Reserve",
};

export const APPLICATION_TEMPLATE_STATUSES = ["draft", "open", "closed"] as const;
export const ApplicationTemplateStatusSchema = z.enum(APPLICATION_TEMPLATE_STATUSES);
export type ApplicationTemplateStatus = z.infer<typeof ApplicationTemplateStatusSchema>;

export const APPLICATION_QUESTION_MAX_LENGTH = 5000;

const FieldBase = z.object({
  id: z.string().uuid(),
  label: z.string().min(1).max(APPLICATION_QUESTION_MAX_LENGTH),
  description: z.string().max(2000).optional(),
  required: z.boolean().default(false),
});

const TextConstraints = {
  minLength: z.number().int().nonnegative().max(10_000).optional(),
  maxLength: z.number().int().positive().max(10_000).optional(),
  minSentences: z.number().int().nonnegative().max(500).optional(),
  maxSentences: z.number().int().positive().max(500).optional(),
};

const TextField = FieldBase.extend({ type: z.literal("short_text"), ...TextConstraints });
const ParagraphField = FieldBase.extend({ type: z.literal("long_text"), ...TextConstraints });
const EmailField = FieldBase.extend({ type: z.literal("email") });
const NumberField = FieldBase.extend({
  type: z.literal("number"),
  min: z.number().optional(),
  max: z.number().optional(),
});
const DateField = FieldBase.extend({ type: z.literal("date") });
const ChoiceField = FieldBase.extend({
  type: z.literal("single_choice"),
  options: z.array(z.string().min(1).max(200)).min(1).max(50),
});
const MultiChoiceField = FieldBase.extend({
  type: z.literal("multi_choice"),
  options: z.array(z.string().min(1).max(200)).min(1).max(50),
  minSelections: z.number().int().nonnegative().optional(),
  maxSelections: z.number().int().positive().optional(),
});
const SectionField = FieldBase.extend({ type: z.literal("section_header") });

export const ApplicationFieldSchema = z.discriminatedUnion("type", [
  TextField,
  ParagraphField,
  EmailField,
  NumberField,
  DateField,
  ChoiceField,
  MultiChoiceField,
  SectionField,
]);
export type ApplicationField = z.infer<typeof ApplicationFieldSchema>;
export const ApplicationFieldsSchema = z.array(ApplicationFieldSchema).min(1).max(100);

export const ApplicationTemplateSchema = z.object({
  id: z.number().int(),
  type: ApplicationTemplateTypeSchema,
  title: z.string(),
  description: z.string().nullable(),
  targetDepartmentId: z.number().int().positive().nullable(),
  status: ApplicationTemplateStatusSchema,
  fields: ApplicationFieldsSchema,
  createdById: z.number().int().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ApplicationTemplate = z.infer<typeof ApplicationTemplateSchema>;

export const CreateApplicationTemplateSchema = ApplicationTemplateSchema.pick({
  type: true,
  title: true,
  description: true,
  targetDepartmentId: true,
  status: true,
  fields: true,
}).extend({ title: z.string().min(1).max(200), description: z.string().max(2000).nullable() });
export type CreateApplicationTemplate = z.infer<typeof CreateApplicationTemplateSchema>;
export const PatchApplicationTemplateSchema = CreateApplicationTemplateSchema.partial().refine(
  (body) => Object.keys(body).length > 0,
  { message: "At least one field is required" },
);

export const ApplicationAnswerSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), value: z.string() }),
  z.object({ type: z.literal("number"), value: z.number() }),
  z.object({ type: z.literal("choice"), value: z.string() }),
  z.object({ type: z.literal("choices"), value: z.array(z.string()) }),
  z.object({ type: z.literal("date"), value: z.string() }),
]);
export type ApplicationAnswer = z.infer<typeof ApplicationAnswerSchema>;
export const ApplicationAnswersSchema = z.record(z.string().uuid(), ApplicationAnswerSchema);
export type ApplicationAnswers = z.infer<typeof ApplicationAnswersSchema>;

function decodeApplicationJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    throw new Error("Application data could not be loaded because its stored JSON is invalid.");
  }
}

/** MariaDB JSON columns may be returned as text rather than decoded values. */
export function parseApplicationFields(value: unknown): ApplicationField[] {
  return ApplicationFieldsSchema.parse(decodeApplicationJson(value));
}

export function parseApplicationAnswers(value: unknown): ApplicationAnswers {
  return ApplicationAnswersSchema.parse(decodeApplicationJson(value));
}

export const APPLICATION_STATUSES = [
  "pending",
  "revision_requested",
  "approved",
  "denied",
  "closed",
  "withdrawn",
] as const;
export const ApplicationStatusSchema = z.enum(APPLICATION_STATUSES);
export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>;

export const APPLICATION_REVIEW_SIDES = ["source_department", "target_department"] as const;
export const ApplicationReviewSideSchema = z.enum(APPLICATION_REVIEW_SIDES);
export type ApplicationReviewSide = z.infer<typeof ApplicationReviewSideSchema>;
export const APPLICATION_DECISIONS = ["approved", "denied"] as const;
export const ApplicationDecisionSchema = z.enum(APPLICATION_DECISIONS);
export type ApplicationDecision = z.infer<typeof ApplicationDecisionSchema>;

export const ApplicationReviewSchema = z.object({
  id: z.number().int(),
  round: z.number().int(),
  side: ApplicationReviewSideSchema,
  reviewerId: z.number().int(),
  reviewerName: z.string().nullable(),
  reviewerRole: RoleSchema.nullable().optional(),
  decision: ApplicationDecisionSchema,
  note: z.string().nullable(),
  stipulations: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type ApplicationReview = z.infer<typeof ApplicationReviewSchema>;

export const ApplicationMessageSchema = z.object({
  id: z.number().int(),
  authorId: z.number().int(),
  authorName: z.string().nullable(),
  authorRole: RoleSchema.nullable().optional(),
  authorDepartmentName: z.string().nullable().optional(),
  type: z.enum(["reply", "revision_request"]),
  body: z.string(),
  createdAt: z.string(),
});
export type ApplicationMessage = z.infer<typeof ApplicationMessageSchema>;

export const ApplicationSchema = z.object({
  id: z.number().int(),
  templateId: z.number().int(),
  templateTitle: z.string(),
  type: ApplicationTypeSchema,
  applicantId: z.number().int(),
  applicantName: z.string().nullable(),
  applicantWebsiteId: z.string().nullable().optional(),
  applicantRole: RoleSchema.nullable().optional(),
  sourceDepartmentId: z.number().int(),
  sourceDepartmentName: z.string(),
  targetDepartmentId: z.number().int(),
  targetDepartmentName: z.string(),
  status: ApplicationStatusSchema,
  reviewRound: z.number().int(),
  fields: ApplicationFieldsSchema,
  answers: ApplicationAnswersSchema,
  reviews: z.array(ApplicationReviewSchema),
  messages: z.array(ApplicationMessageSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Application = z.infer<typeof ApplicationSchema>;

export const ApplicationHistoryItemSchema = ApplicationSchema.pick({
  id: true,
  templateTitle: true,
  type: true,
  status: true,
  createdAt: true,
  reviewRound: true,
}).extend({ reviews: z.array(ApplicationReviewSchema.omit({ note: true, stipulations: true })) });
export type ApplicationHistoryItem = z.infer<typeof ApplicationHistoryItemSchema>;

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending: "Pending",
  revision_requested: "Pending",
  approved: "Approved",
  denied: "Denied",
  closed: "Closed",
  withdrawn: "Withdrawn",
};

export function applicationPlacementLabel(
  application: Pick<Application, "type" | "sourceDepartmentName" | "targetDepartmentName">,
): string {
  return application.type === "reserve"
    ? `Reserve · ${application.targetDepartmentName}`
    : `Transfer · ${application.sourceDepartmentName} → ${application.targetDepartmentName}`;
}

export function applicationProgressLabel(
  application: Pick<
    Application,
    "type" | "status" | "sourceDepartmentName" | "targetDepartmentName" | "reviews" | "reviewRound"
  >,
): string {
  if (!["pending", "revision_requested"].includes(application.status))
    return APPLICATION_STATUS_LABELS[application.status];
  const needed =
    application.type === "reserve"
      ? ["target_department"]
      : ["source_department", "target_department"];
  const pending = needed.filter(
    (side) =>
      !(application.reviews ?? []).some(
        (review) =>
          review.round === application.reviewRound &&
          review.side === side &&
          review.decision === "approved",
      ),
  );
  return `Pending ${pending.map((side) => (side === "source_department" ? application.sourceDepartmentName : application.targetDepartmentName)).join(" and ")} approval`;
}

export const ApplicationActivityItemSchema = z.object({
  id: z.string(),
  type: z.enum(["submitted", "approved", "denied", "withdrawn", "closed"]),
  actorId: z.number().int().nullable(),
  actorName: z.string().nullable(),
  createdAt: z.string(),
  departmentName: z.string().nullable(),
});
export type ApplicationActivityItem = z.infer<typeof ApplicationActivityItemSchema>;

export const CreateApplicationSchema = z.object({
  templateId: z.number().int().positive(),
  type: ApplicationTypeSchema.optional(),
  answers: ApplicationAnswersSchema,
});
export const ApplicationReplySchema = z.object({
  body: z.string().trim().min(1).max(5000),
  requestRevision: z.boolean().optional().default(false),
});
export const ApplicationDecisionBodySchema = z.object({
  decision: ApplicationDecisionSchema,
  note: z.string().trim().max(2000).nullable().optional(),
  stipulations: z.string().trim().max(2000).nullable().optional(),
});
export const ApplicationPrivateNoteBodySchema = z.object({
  body: z.string().trim().min(1).max(5000),
});
export const ApplicationRevisionBodySchema = z.object({ body: z.string().trim().min(1).max(5000) });
export const ApplicationResubmitBodySchema = z.object({ answers: ApplicationAnswersSchema });
export const ApplicationPrivateNoteSchema = z.object({
  id: z.number().int(),
  authorId: z.number().int(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.string(),
});

export const ApplicationDepartmentChoiceSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  isPrimary: z.boolean().optional(),
});
export type ApplicationDepartmentChoice = z.infer<typeof ApplicationDepartmentChoiceSchema>;
